// src/components/trades/components/TradeProposalCard.js
//
// Extracted from inline definition in TradeCenter.js per Tier 2 #7
// commit 7b (refactor only — no behavior change).
import React, { useState } from 'react';
import {
  ArrowRightLeft, CheckCircle, XCircle, Clock, AlertCircle,
  ChevronDown, ChevronUp, TrendingUp, TrendingDown,
} from 'lucide-react';
import { dynastyTheme } from '../../../services/colorService';
import TradeAssetsList from './TradeAssetsList';

const TradeProposalCard = ({ trade, type, onAccept, onReject, onCancel }) => {
  const [expanded, setExpanded] = useState(false);

  const getStatusBadge = () => {
    if (type === 'pending') {
      return (
        <span className={dynastyTheme.components.badge.warning}>
          <Clock className="w-3 h-3 inline mr-1" />
          Waiting for Commissioner
        </span>
      );
    }
    if (type === 'incoming') {
      return (
        <span className={dynastyTheme.components.badge.error}>
          <AlertCircle className="w-3 h-3 inline mr-1" />
          Action Required
        </span>
      );
    }
    return (
      <span className={dynastyTheme.components.badge.info}>
        <Clock className="w-3 h-3 inline mr-1" />
        Awaiting Response
      </span>
    );
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className={`
      rounded-lg overflow-hidden ${dynastyTheme.classes.transition}
      ${type === 'incoming'
        ? `border-2 ${dynastyTheme.classes.border.error} bg-red-500/5`
        : `border ${dynastyTheme.classes.border.neutral} ${dynastyTheme.classes.bg.darkCard}`
      }
      hover:${dynastyTheme.classes.border.primary}
    `}>
      {/* Header */}
      <div className={`p-4 border-b ${dynastyTheme.classes.border.neutral}`}>
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <ArrowRightLeft className={`w-5 h-5 ${dynastyTheme.classes.text.primary}`} />
              <div>
                <div className={`font-bold ${dynastyTheme.classes.text.white} text-lg`}>
                  {type === 'incoming' ? 'Trade Proposal From' : 'Trade Proposal To'} {trade.other_team?.team_name}
                </div>
                <div className={`text-sm ${dynastyTheme.classes.text.neutralLight} mt-0.5`}>
                  {formatDate(trade.proposed_at)}
                </div>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {getStatusBadge()}
            <button
              onClick={() => setExpanded(!expanded)}
              className={`p-2 rounded-lg ${dynastyTheme.classes.bg.darkLighter} hover:bg-gray-600 ${dynastyTheme.classes.transition}`}
            >
              {expanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Trade Details */}
      {expanded && (
        <div className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            {/* Left Side */}
            <div className={`p-4 rounded-lg ${dynastyTheme.classes.bg.darkLighter}`}>
              <div className="flex items-center gap-2 mb-3">
                <TrendingDown className={`w-5 h-5 ${dynastyTheme.classes.text.error}`} />
                <h4 className={`text-sm font-bold ${dynastyTheme.classes.text.error}`}>
                  {type === 'incoming' ? 'THEY GIVE' : 'YOU GIVE'}
                </h4>
              </div>
              <TradeAssetsList
                assets={type === 'incoming' ? trade.they_give : trade.i_give}
              />
            </div>

            {/* Right Side */}
            <div className={`p-4 rounded-lg ${dynastyTheme.classes.bg.darkLighter}`}>
              <div className="flex items-center gap-2 mb-3">
                <TrendingUp className={`w-5 h-5 ${dynastyTheme.classes.text.success}`} />
                <h4 className={`text-sm font-bold ${dynastyTheme.classes.text.success}`}>
                  {type === 'incoming' ? 'YOU GIVE' : 'YOU GET'}
                </h4>
              </div>
              <TradeAssetsList
                assets={type === 'incoming' ? trade.they_want : trade.i_receive}
              />
            </div>
          </div>

          {/* Notes */}
          {trade.notes && (
            <div className={`p-4 ${dynastyTheme.classes.bg.darkLighter} rounded-lg mb-4 border-l-4 ${dynastyTheme.classes.border.primary}`}>
              <div className={`text-xs font-bold ${dynastyTheme.classes.text.neutralLight} mb-2`}>
                💬 TRADE MESSAGE
              </div>
              <div className={`text-sm ${dynastyTheme.classes.text.white} italic`}>
                "{trade.notes}"
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3">
            {type === 'incoming' && (
              <>
                <button
                  onClick={() => onAccept(trade.trade_id)}
                  className={`flex-1 flex items-center justify-center gap-2 ${dynastyTheme.utils.getComponent('button', 'primary', 'md')}`}
                >
                  <CheckCircle className="w-5 h-5" />
                  Accept Trade
                </button>
                <button
                  onClick={() => onReject(trade.trade_id)}
                  className={`flex-1 flex items-center justify-center gap-2 ${dynastyTheme.utils.getComponent('button', 'danger', 'md')}`}
                >
                  <XCircle className="w-5 h-5" />
                  Reject Trade
                </button>
              </>
            )}

            {type === 'outgoing' && trade.status === 'proposed' && (
              <button
                onClick={() => onCancel(trade.trade_id)}
                className={dynastyTheme.utils.getComponent('button', 'secondary', 'md')}
              >
                <XCircle className="w-4 h-4" />
                Cancel Proposal
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TradeProposalCard;
